import React, { useMemo } from 'react';
import Shortcuts from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcuts/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useRouterActions } from '@atlassian/react-resource-router';
import {
	useSelectedIssueKey,
	useSelectedRow,
} from '@atlassian/jira-issue-table-hierarchy/src/controllers/hierarchy/index.tsx';
import { useIsGridNavigationEnabled } from '../../controllers/features/selectors.tsx';

type Props = {
	onIssueLinkSelected?: (issueKey: string) => boolean;
};

const KeyboardShortcuts = ({ onIssueLinkSelected }: Props) => {
	const { push } = useRouterActions();
	const isGridNavigationEnabled = useIsGridNavigationEnabled();
	const [selectedRowIndex, { selectNextRow, selectPrevRow }] = useSelectedRow();
	const [selectedIssueKey] = useSelectedIssueKey();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const keyMap = useMemo(() => {
		const navigateToIssue = (issueKey: string) => {
			push(`/browse/${issueKey}`);
		};

		return {
			arrowup: {
				callback: (e?: KeyboardEvent) => {
					// Prevent native browser scrolling as we provide auto-scroll functionality via scrollIntoView
					e?.preventDefault();
					if (isGridNavigationEnabled) return;
					selectPrevRow();
				},
			},
			arrowdown: {
				callback: (e?: KeyboardEvent) => {
					// Prevent native browser scrolling as we provide auto-scroll functionality via scrollIntoView
					e?.preventDefault();
					if (isGridNavigationEnabled) return;
					selectNextRow();
				},
			},
			k: {
				callback: () => {
					if (isGridNavigationEnabled) return;
					fireUIAnalytics(createAnalyticsEvent({}), 'rowItem pressed', 'navigateRowUp', {
						rowIndex: selectedRowIndex,
						keyPressed: 'k',
					});
					selectPrevRow();
				},
			},
			j: {
				callback: () => {
					if (isGridNavigationEnabled) return;
					fireUIAnalytics(createAnalyticsEvent({}), 'rowItem pressed', 'navigateRowDown', {
						rowIndex: selectedRowIndex,
						keyPressed: 'j',
					});
					selectNextRow();
				},
			},
			o: {
				callback: () => {
					fireUIAnalytics(createAnalyticsEvent({}), 'link clicked', 'tableIssueLink', {
						fieldType: 'unknown',
						keyPressed: 'o',
						tableRowNumber: selectedRowIndex,
						keyboardShortcut: true,
					});

					if (typeof selectedIssueKey === 'string' && selectedIssueKey.length > 0) {
						const handled =
							onIssueLinkSelected !== undefined && onIssueLinkSelected(selectedIssueKey);
						if (!handled) {
							navigateToIssue(selectedIssueKey);
						}
					}
				},
			},
			z: {
				callback: () => {
					fireUIAnalytics(createAnalyticsEvent({}), 'link clicked', 'tableIssueLink', {
						fieldType: 'unknown',
						keyPressed: 'z',
						tableRowNumber: selectedRowIndex,
						keyboardShortcut: true,
					});

					if (typeof selectedIssueKey === 'string' && selectedIssueKey.length > 0) {
						const handled =
							onIssueLinkSelected !== undefined && onIssueLinkSelected(selectedIssueKey);
						if (!handled) {
							navigateToIssue(selectedIssueKey);
						}
					}
				},
			},
		};
	}, [
		push,
		selectPrevRow,
		selectNextRow,
		createAnalyticsEvent,
		selectedRowIndex,
		selectedIssueKey,
		onIssueLinkSelected,
		isGridNavigationEnabled,
	]);

	return <Shortcuts keyMap={keyMap} />;
};

export default KeyboardShortcuts;
